import { styled, SxProps, Theme } from "@mui/material";
import TagChipWithTooltip from "components/molecules/TagChipWithTooltip";
import { Asset } from "components/pages/AssetManagement/AssetOverview";
import PLPill from "components/patternLib/PLPill";
import PLTooltip from "components/patternLib/PLTooltip";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

export interface TagChipsProps<T> {
  customSx?: SxProps<Theme> | undefined;
  mappingAssetTag?: Asset["mappingAssetTag"];
  mappingDeviceTag?: T[];
}

// Minimal ResizeObserver typing (TS 4.1 lib.dom predates ResizeObserver types).
interface ResizeObserverLike {
  disconnect: () => void;
  observe: (target: Element) => void;
}
type ResizeObserverCtor = new (callback: () => void) => ResizeObserverLike;

// Space reserved between chips (matches TagChipWithTooltip margin-right).
const CHIP_GAP = 8;
// Small buffer so the last visible chip never touches the container edge.
const SAFETY_MARGIN = 4;

const ChipsContainerSC = styled("div")(
  () => `
      position: relative;
      display: inline-flex;
      flex-wrap: nowrap;
      align-items: center;
      width: 100%;
      max-width: 100%;
      overflow: hidden;
      align-content: center;
`
);

// Off-screen layer used to measure the natural width of each chip.
// Kept at natural height (no collapsing) so the PLPill web components lay out
// and report their real width.
const MeasureLayerSC = styled("div")(
  () => `
      position: absolute;
      top: 0;
      left: 0;
      display: inline-flex;
      flex-wrap: nowrap;
      visibility: hidden;
      pointer-events: none;
      white-space: nowrap;
      display: flex;
      align-content: center;
`
);

const ExtraChipsCountSC = styled("div")(
  () => `
      width: fit-content;
      white-space: pre-line;
      z-index: 999999999;
`
);

export const TagChips = <
  T extends {
    id: string;
    tag: { tagValue: string };
  }
>({
  mappingAssetTag,
  mappingDeviceTag,
  customSx,
}: TagChipsProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  // Normalize both mapping shapes into a single list of { id, tagValue }.
  const items = mappingAssetTag
    ? mappingAssetTag.map((mapping) => ({ id: mapping.id, tagValue: mapping.assetTag.tagValue }))
    : (mappingDeviceTag ?? []).map((mapping) => ({ id: mapping.id, tagValue: mapping.tag.tagValue }));

  const [visibleCount, setVisibleCount] = useState<number>(items.length);

  const tagKey = items.map((item) => item.id).join("|");

  const computeVisibleCount = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const available = container.getBoundingClientRect().width - SAFETY_MARGIN;
    if (available <= 0) return;

    const chipNodes = Array.from(measure.querySelectorAll<HTMLElement>("[data-chip]"));
    const extraNode = measure.querySelector<HTMLElement>("[data-extra]");
    if (chipNodes.length === 0) return;

    const chipWidths = chipNodes.map((node) => node.getBoundingClientRect().width);
    // Web components (PLPill) render asynchronously; retry once they have a width.
    if (chipWidths.some((width) => width === 0)) {
      requestAnimationFrame(computeVisibleCount);
      return;
    }
    const extraWidth = extraNode ? extraNode.getBoundingClientRect().width : 0;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chipWidths.length; i++) {
      used += chipWidths[i] + CHIP_GAP;
      if (used <= available) count++;
      else break;
    }

    // If some chips are hidden, reserve room for the "+N" indicator.
    if (count < chipWidths.length) {
      let widthWithExtra =
        chipWidths.slice(0, count).reduce((sum, width) => sum + width + CHIP_GAP, 0) + extraWidth + CHIP_GAP;
      while (count > 0 && widthWithExtra > available) {
        count--;
        widthWithExtra =
          chipWidths.slice(0, count).reduce((sum, width) => sum + width + CHIP_GAP, 0) + extraWidth + CHIP_GAP;
      }
    }

    setVisibleCount(count);
  }, []);

  useLayoutEffect(() => {
    computeVisibleCount();
    // Re-measure after the PLPill web components have hydrated their width.
    const raf = requestAnimationFrame(computeVisibleCount);
    const timer = window.setTimeout(computeVisibleCount, 150);
    window.addEventListener("resize", computeVisibleCount);

    // Observe the container itself so layout changes that don't fire a window
    // resize (e.g. sidebar collapse/expand, panel toggles) still recompute.
    const ResizeObserverImpl = ((window as unknown) as { ResizeObserver?: ResizeObserverCtor }).ResizeObserver;
    const container = containerRef.current;
    let observer: ResizeObserverLike | undefined;
    if (ResizeObserverImpl && container) {
      observer = new ResizeObserverImpl(computeVisibleCount);
      observer.observe(container);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      window.removeEventListener("resize", computeVisibleCount);
      observer?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computeVisibleCount, tagKey]);

  const hiddenCount = Math.max(items.length - visibleCount, 0);
  const hiddenTagValues = items.slice(visibleCount).map((item) => item.tagValue);

  const ExtraTags = (length: number) => (
    <ExtraChipsCountSC title={hiddenTagValues.join(", ")}>
      <PLTooltip tooltipPosition="left">
        <PLPill height="22px" label={`+${length}`} noStates />
      </PLTooltip>
    </ExtraChipsCountSC>
  );

  return (
    <ChipsContainerSC ref={containerRef} sx={customSx}>
      {items.slice(0, visibleCount).map((item) => (
        <TagChipWithTooltip key={item.id} tagValue={item.tagValue} />
      ))}
      {hiddenCount > 0 && ExtraTags(hiddenCount)}

      {/* Hidden measurement layer: renders every chip to obtain its natural width. */}
      <MeasureLayerSC ref={measureRef} aria-hidden>
        {items.map((item) => (
          <div data-chip key={`measure-${item.id}`}>
            <TagChipWithTooltip tagValue={item.tagValue} />
          </div>
        ))}
        <div data-extra>
          <ExtraChipsCountSC>
            <PLPill height="22px" label={`+${items.length}`} noStates />
          </ExtraChipsCountSC>
        </div>
      </MeasureLayerSC>
    </ChipsContainerSC>
  );
};
export default TagChips;
